import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Loader2, CheckCircle2, Wrench } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicWorkshopReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState("");
  const antesRef = useRef();
  const despuesRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/workshop/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (phase, ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = "";
    if (files.length === 0) return;
    setUploading(phase);
    setMsg("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("phase", phase);
        await axios.post(`${API}/api/public/workshop/${token}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setMsg(`✓ ${files.length} foto(s) de ${phase.toUpperCase()} subidas correctamente`);
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Error al subir la foto. Intenta de nuevo.");
    } finally { setUploading(null); }
  };

  const photoUrl = (fid) => `${API}/api/public/workshop/${token}/photo/${fid}`;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: "#0a0f1d", color: "#fca5a5" }}>
      <div><Wrench className="w-10 h-10 mx-auto mb-3 opacity-60" /><div className="font-semibold">{error}</div></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f1d", color: "#94a3b8" }}>Cargando…</div>;

  const antes = data.photos.filter((p) => p.phase === "antes");
  const despues = data.photos.filter((p) => p.phase === "despues");
  const canUpload = data.status === "aprobada";

  return (
    <div className="min-h-screen pb-16" style={{ background: "#0a0f1d", color: "#e2e8f0", paddingTop: "env(safe-area-inset-top)" }} data-testid="public-workshop-page">
      <div className="px-4 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: "#22d3ee" }}>// ORDEN DE TALLER</div>
        <h1 className="font-bold text-xl">{data.folio} — {data.vehicle?.brand} {data.vehicle?.model}</h1>
        <div className="text-xs text-slate-400 font-mono mt-0.5">PLACAS {data.vehicle?.plates} {data.vehicle?.vin ? `· SERIE ${data.vehicle.vin}` : ""}</div>
        {data.service_description && <div className="text-sm mt-1 text-slate-300">{data.service_description}</div>}
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {data.status === "terminada" && (
          <div className="px-3 py-2 rounded text-sm flex items-center gap-2" style={{ background: "rgba(34,211,238,0.12)", border: "1px solid #22d3ee", color: "#a5f3fc" }}>
            <CheckCircle2 className="w-4 h-4" /> Trabajo terminado. Las fotos quedaron registradas.
          </div>
        )}
        {!canUpload && data.status !== "terminada" && (
          <div className="px-3 py-2 rounded text-sm" style={{ background: "rgba(245,158,11,0.12)", border: "1px solid #f59e0b", color: "#fcd34d" }}>
            Esta orden aún no está aprobada para trabajar.
          </div>
        )}

        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: "#94a3b8" }}>TRABAJOS A REALIZAR</div>
          <ul className="space-y-1">
            {data.items.map((it, i) => (
              <li key={i} className="px-3 py-1.5 rounded text-sm" style={{ background: "rgba(255,255,255,0.05)" }}>{it.concept} <span className="text-slate-500 text-xs">({it.qty} {it.unit})</span></li>
            ))}
          </ul>
        </div>

        {msg && <div className="px-3 py-2 rounded text-sm" style={{ background: msg.startsWith("✓") ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)", border: `1px solid ${msg.startsWith("✓") ? "#16a34a" : "#dc2626"}` }}>{msg}</div>}

        {[["antes", "ANTES", antes, "#dc2626", antesRef], ["despues", "DESPUÉS", despues, "#16a34a", despuesRef]].map(([phase, label, list, color, ref]) => (
          <div key={phase}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm" style={{ color }}>{label} ({list.length})</span>
              {canUpload && (
                <>
                  <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => upload(phase, e)} data-testid={`public-upload-${phase}-input`} />
                  <button
                    data-testid={`public-upload-${phase}-btn`}
                    disabled={!!uploading}
                    onClick={() => ref.current?.click()}
                    className="px-4 py-2 rounded font-semibold text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: color, color: "white" }}
                  >
                    {uploading === phase ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Tomar foto {label}
                  </button>
                </>
              )}
            </div>
            {list.length === 0 && <div className="text-xs text-slate-500 italic">Sin fotos de {label.toLowerCase()} aún.</div>}
            <div className="grid grid-cols-3 gap-2">
              {list.map((p) => (
                <img key={p.file_id} src={photoUrl(p.file_id)} alt={label} className="w-full h-28 object-cover rounded" style={{ border: `2px solid ${color}` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
